import { AbstractControl, FormArray, ValidationErrors, ValidatorFn } from '@angular/forms';
import { RESERVED_SUBDOMAINS } from '../../../shared/utils/constants';

export function timeRangeValidator(control: AbstractControl): ValidationErrors | null {
    const start = control.get('start')?.value;
    const end = control.get('end')?.value;

    if (!start || !end) return null;

    return start < end ? null : { invalidTimeRange: true };
}

export function overlappingRangesValidator(): ValidatorFn {
    return (formArray: AbstractControl): { [key: string]: boolean } | null => {
        const ranges = (formArray as FormArray).controls;
        const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

        const toDayIndices = (from: string, to: string): number[] => {
            const fromIndex = dayOrder.indexOf(from);
            const toIndex = dayOrder.indexOf(to);
            if (fromIndex === -1 || toIndex === -1) return [];

            if (fromIndex <= toIndex) {
                return dayOrder.slice(fromIndex, toIndex + 1).map((day) => dayOrder.indexOf(day));
            } else {
                return [...dayOrder.slice(fromIndex), ...dayOrder.slice(0, toIndex + 1)].map((day) =>
                    dayOrder.indexOf(day)
                );
            }
        };

        const parseTime = (timeStr: string): number => {
            if (!timeStr) return -1;
            const [h, m] = timeStr.split(':').map(Number);
            return h * 60 + m;
        };

        for (let i = 0; i < ranges.length; i++) {
            const groupA = ranges[i];
            const valueA = groupA.value;

            if (!valueA?.daysRange?.from || !valueA?.daysRange?.to || !valueA?.start || !valueA?.end) continue;

            const daysA = toDayIndices(valueA.daysRange.from, valueA.daysRange.to);
            const startA = parseTime(valueA.start);
            const endA = parseTime(valueA.end);

            for (let j = i + 1; j < ranges.length; j++) {
                const groupB = ranges[j];
                const valueB = groupB.value;

                if (!valueB?.daysRange?.from || !valueB?.daysRange?.to || !valueB?.start || !valueB?.end) continue;

                const daysB = toDayIndices(valueB.daysRange.from, valueB.daysRange.to);
                const startB = parseTime(valueB.start);
                const endB = parseTime(valueB.end);

                const sharedDays = daysA.some((day) => daysB.includes(day));
                const overlappingTime = startA < endB && endA > startB;

                if (sharedDays && overlappingTime) {
                    return { overlappingRanges: true };
                }
            }
        }

        return null;
    };
}

export function subdomainValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value: string = control.value?.trim() ?? '';

        if (!value) return null;

        const labelRegex = /^[a-z0-9]([a-z0-9.-]{0,61}[a-z0-9])?$/;
        if (!labelRegex.test(value)) {
            return {
                subdomainFormat:
                    'Only lowercase letters, numbers, and hyphens. Must start and end with a letter or number.',
            };
        }

        if (RESERVED_SUBDOMAINS.includes(value)) {
            return { subdomainReserved: `"${value}" is a reserved subdomain and cannot be used.` };
        }

        return null;
    };
}

export function customDomainValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value: string = control.value?.trim() ?? '';

        if (!value) return null;

        if (/^https?:\/\//i.test(value)) {
            return { customDomainProtocol: 'Do not include the protocol (https://).' };
        }

        if (/[/?#]/.test(value)) {
            return { customDomainPath: 'Do not include paths or query strings.' };
        }

        if (!value.includes('.')) {
            return { customDomainNoDot: 'Must be a fully qualified domain (e.g. pay.customer.com).' };
        }

        if (value.length > 253) {
            return { customDomainLength: 'Domain must be 253 characters or fewer.' };
        }

        const labels = value.split('.');
        const labelRegex = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/i;
        for (const label of labels) {
            if (!label || label.length > 63 || !labelRegex.test(label)) {
                return {
                    customDomainLabel: `"${label}" is not a valid domain label. Use letters, numbers, and hyphens only.`,
                };
            }
        }

        return null;
    };
}
